// ============================================================================
// GoalsPanel — list a client's goals with a COMPUTED progress bar + a linear
// projection (ETA) derived from the metrics series, plus create / edit / delete.
//   • current value  ← the latest point of the goal's matching metric series
//   • progress %     ← lib.progressPct (baseline → current → target)
//   • projected ETA  ← lib.weeklyRate + lib.projectDate (least-squares trend)
// Optional-first / no-fake-numbers: progress and ETA render only when they are
// actually computable; otherwise the goal still shows its target + a hint.
// Delete asks for an inline confirm so a tap can't nuke a goal by accident.
// ============================================================================
import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardHeader, EmptyState } from "../../components";
import { IconPlus } from "../../components/icons";
import { useDeleteGoal } from "../../hooks/goals";
import type { Goal, MetricsSummary } from "../../api";
import { cn } from "../../lib/cn";
import { GoalForm } from "./GoalForm";
import {
  fmtDate,
  fmtNum,
  goalSeriesKey,
  inferDirection,
  progressPct,
  projectDate,
  weeklyRate,
} from "./lib";

interface GoalsPanelProps {
  clientId: number;
  goals: Goal[];
  metrics: MetricsSummary | undefined;
}

export function GoalsPanel({ clientId, goals, metrics }: GoalsPanelProps) {
  const { t } = useTranslation("clientDetail");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Goal | null>(null);

  const active = goals.filter((g) => g.status !== "abandoned");

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (g: Goal) => {
    setEditing(g);
    setFormOpen(true);
  };

  return (
    <Card>
      <CardHeader
        title={t("goals.title")}
        subtitle={t("goals.subtitle")}
        action={
          <Button size="sm" onClick={openNew} iconLeft={<IconPlus width={16} height={16} />}>
            {t("goals.add")}
          </Button>
        }
      />

      {active.length === 0 ? (
        <EmptyState
          title={t("goals.emptyTitle")}
          description={t("goals.emptyBlurb")}
          action={
            <Button size="sm" onClick={openNew} iconLeft={<IconPlus width={16} height={16} />}>
              {t("goals.add")}
            </Button>
          }
          className="!py-12"
        />
      ) : (
        <ul className="space-y-3">
          <AnimatePresence initial={false}>
            {active.map((goal) => (
              <GoalRow
                key={goal.id}
                goal={goal}
                metrics={metrics}
                onEdit={() => openEdit(goal)}
              />
            ))}
          </AnimatePresence>
        </ul>
      )}

      <GoalForm
        clientId={clientId}
        goal={editing}
        open={formOpen}
        onClose={() => setFormOpen(false)}
      />
    </Card>
  );
}

function GoalRow({
  goal,
  metrics,
  onEdit,
}: {
  goal: Goal;
  metrics: MetricsSummary | undefined;
  onEdit: () => void;
}) {
  const { t } = useTranslation("clientDetail");
  const reduce = useReducedMotion();
  const del = useDeleteGoal();
  const [confirming, setConfirming] = useState(false);

  const direction = inferDirection(goal);
  const seriesKey = goalSeriesKey(goal.metric_key);
  const series = metrics?.series[seriesKey] ?? [];
  const current = series.length > 0 ? series[series.length - 1].y : null;

  const pct = progressPct(
    goal.baseline_value,
    current,
    goal.target_value,
    direction,
  );
  const clampedPct = pct != null ? Math.max(0, Math.min(100, pct)) : null;
  const achieved = goal.status === "achieved" || (pct != null && pct >= 100);

  const rate = weeklyRate(series);
  const projection =
    current != null && goal.target_value != null
      ? projectDate(current, goal.target_value, rate)
      : null;

  const metricLabel = t(`trends.tab.${seriesKey}`);

  return (
    <motion.li
      layout={!reduce}
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, x: -12 }}
      transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
      className="rounded-md bg-surface p-4 ring-1 ring-line"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-display text-base font-bold text-body">
              {goal.kind?.trim() || t("goals.untitled")}
            </h4>
            {achieved ? (
              <Badge tone="success" solid>
                {t("goals.achieved")}
              </Badge>
            ) : (
              <Badge tone={direction === "reduce" ? "volt" : "ember"}>
                {t(`goals.${direction}`)}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-sm text-body-soft">
            {goal.target_value != null
              ? t("goals.targetSummary", {
                  metric: metricLabel,
                  value: fmtNum(goal.target_value, 1),
                })
              : t("goals.noTarget")}
            {goal.target_date && (
              <span className="text-body-faint">
                {" · "}
                {t("goals.byDate", { date: fmtDate(goal.target_date) })}
              </span>
            )}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            aria-label={t("actions.edit")}
            className="grid h-8 w-8 place-items-center rounded-pill text-body-muted transition-colors hover:bg-surface-sunken hover:text-body"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
              <path
                d="M4 20h4l10-10-4-4L4 16v4Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path d="m13.5 6.5 4 4" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setConfirming((c) => !c)}
            aria-label={t("actions.delete")}
            className="grid h-8 w-8 place-items-center rounded-pill text-body-muted transition-colors hover:bg-coral-soft hover:text-volt-600"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
              <path
                d="M5 7h14M9 7V5h6v2m-8 0 1 13h8l1-13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {clampedPct != null ? (
        <div className="mt-3">
          <div className="mb-1 flex items-baseline justify-between text-xs">
            <span className="font-bold text-body-soft">
              {current != null &&
                t("goals.current", { value: fmtNum(current, 1) })}
            </span>
            <span className="nums font-display text-sm font-extrabold text-body">
              {fmtNum(clampedPct, 0)}%
            </span>
          </div>
          <div className="h-2.5 overflow-hidden rounded-pill bg-surface-sunken">
            <motion.div
              className={cn(
                "h-full rounded-pill",
                achieved ? "bg-grad-lime" : "bg-grad-coral",
              )}
              initial={reduce ? false : { width: 0 }}
              animate={{ width: `${clampedPct}%` }}
              transition={{ duration: 0.8, ease: [0.22, 0.61, 0.36, 1] }}
            />
          </div>
        </div>
      ) : (
        goal.target_value != null && (
          <p className="mt-3 text-xs text-body-faint">{t("goals.noProgressYet")}</p>
        )
      )}

      {/* Projection */}
      {projection && !achieved && (
        <p className="mt-2 text-xs text-body-soft">
          <span className="font-bold text-ember-600">{t("goals.projection")}</span>{" "}
          {t("goals.projectionDetail", {
            date: fmtDate(projection.etaIso),
            weeks: Math.ceil(projection.weeks),
          })}
        </p>
      )}

      {/* Delete confirm */}
      <AnimatePresence>
        {confirming && (
          <motion.div
            initial={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="mt-3 flex items-center justify-between gap-3 rounded-sm bg-coral-soft p-3">
              <span className="text-sm font-medium text-body">
                {t("goals.confirmDelete")}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirming(false)}
                  disabled={del.isPending}
                >
                  {t("actions.cancel")}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() =>
                    del.mutate(goal.id, { onSuccess: () => setConfirming(false) })
                  }
                  disabled={del.isPending}
                >
                  {t("actions.delete")}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.li>
  );
}
